import {
  carbonConvert,
  CarbonMeasurementSystem,
  CarbonUnit,
  UnitSelector,
} from "common/utils/units/units";
import { InputAdornment, TextFieldProps } from "@mui/material";
import { NumberInput } from "./NumberInput";
import { roundToNPlaces } from "common/utils/numbers";
import { useTranslation } from "react-i18next";
import React, { FC } from "react";

export interface DimensionedNumberInputProps {
  label: string;
  storedValue: number;
  storedUnit: CarbonUnit;
  disabled?: boolean;
  displayUnits: UnitSelector;
  localSystem: CarbonMeasurementSystem;
  /**
   * because of unit system conversions, integer input values get turned into
   * messily non-integral internal representations and lose precision,
   * introducing rounding errors.  to increase user confidence, this helper
   * rounds off the last decimal places of the displayed value, in the thought
   * that the user was unlikely to have entered e.g. an implement width of
   * exactly 34.00000706 feet.  the appropriate number to choose here depends
   * on the magnitude of the value you expect to be entered; the default (4)
   * should work well for values between ~1 and ~1000, but you should consider
   * decreasing it by 1 for every order of magnitude above that, or increasing
   * it if you're expecting a user to enter a value small enough that 0.0001 is
   * a significant fraction.
   */
  roundDisplayToNPlaces?: number;
  onChange: (storedValue: number | undefined) => void;
  minStoredValue?: number;
  maxStoredValue?: number;
  error?: string;
  textFieldProps?: Partial<TextFieldProps>;
}

export const DimensionedNumberInput: FC<DimensionedNumberInputProps> = ({
  label,
  storedValue,
  storedUnit,
  disabled,
  displayUnits,
  localSystem,
  roundDisplayToNPlaces = 4,
  onChange,
  minStoredValue,
  maxStoredValue,
  error,
  textFieldProps,
}) => {
  const { t } = useTranslation();
  const displayUnit = displayUnits[localSystem];
  const rawDisplayValue = carbonConvert(storedValue, storedUnit, displayUnit);

  const formattedDisplayValue = roundToNPlaces(
    rawDisplayValue,
    roundDisplayToNPlaces
  );

  return (
    <NumberInput
      label={label}
      value={formattedDisplayValue}
      onChange={(e) => {
        if (e === undefined) {
          onChange(undefined);
        } else {
          onChange(carbonConvert(e, displayUnit, storedUnit));
        }
      }}
      min={
        minStoredValue === undefined
          ? undefined
          : carbonConvert(minStoredValue, storedUnit, displayUnit)
      }
      max={
        maxStoredValue === undefined
          ? undefined
          : carbonConvert(maxStoredValue, storedUnit, displayUnit)
      }
      error={error}
      textFieldProps={{
        ...textFieldProps,
        disabled,
        slotProps: {
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {t(`utils.units.${displayUnit}`)}
              </InputAdornment>
            ),
          },
        },
      }}
    />
  );
};
